import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import Dropdown from 'components/Toolbar/Shared/Dropdown';
import {
  KeyboardKey,
  LAST_VISITED_SLIDE,
  NEXT_SLIDE,
  PREVIOUS_SLIDE,
  ProjectsConfig,
  SCREEN_LINK_PREFIX,
  UUID_PATTERN,
} from 'const';
import { activeSurfaceId as activeSurfaceIdSelector, projectType as projectTypeSelector } from 'containers/Project/selectors';
import { orderedSurfaces as orderedSurfacesSelector } from 'containers/Surfaces/selectors';
import { validateEventKey } from 'containers/UndoRedoControl/utils';
import { ScreenMap, ScreensMap } from 'models';
import { appendProtocol } from 'utils/appendProtocol';
import { removeZeroWidthSpace } from 'utils/chars';
import { intlGet } from 'utils/intlGet';
import { isStartingWithProtocol } from 'utils/isStartingWithProtocol';
import { LinkDropdownProps } from './models';
import styles from './styles.module.scss';

const editorToolbarLink = 'EditorToolbar.Link';

const isInternalLink = (link: string, screens: ScreensMap): boolean => {
  const screenIdFromLink = link.replace(SCREEN_LINK_PREFIX, '');

  return !isStartingWithProtocol(link) && (
    [NEXT_SLIDE, PREVIOUS_SLIDE, LAST_VISITED_SLIDE].includes(link) ||
    screens.keySeq().toArray().includes(screenIdFromLink)
  );
};

const LinkDropdown: React.FunctionComponent<LinkDropdownProps> = (props) => {
  const activeScreenId = useSelector(activeSurfaceIdSelector);
  const projectType = useSelector(projectTypeSelector);
  const screens = useSelector(orderedSurfacesSelector);
  const {
    enableInternalLinks = true,
    link,
    toggleDropdown,
    toggleLink,
    dataTestId = 'link',
  } = props;
  const { enableProjectLinks, enableLinkProtocolAppending, externalLinkPlaceholder } = ProjectsConfig[projectType];
  const isExternalLink = !isInternalLink(link, screens as ScreensMap);
  const activeScreenIndex = screens.keySeq().findIndex(id => id === activeScreenId);
  const showInternalLinks = enableInternalLinks && enableProjectLinks && screens.size > 1;
  const showLinkToPreviousScreen = activeScreenIndex > 0;
  const showLinkToNextScreen = activeScreenIndex < screens.size - 1;

  const [linkText, setLinkText] = React.useState(isExternalLink ? link : '');
  const screensRef = React.useRef(screens);
  const linkRef = React.useRef(linkText);

  React.useEffect(() => {
    setLinkText(link);
  }, [link]);

  React.useEffect(
    () => { linkRef.current = linkText; screensRef.current = screens; },
    [linkText, screens],
  );

  React.useEffect(
    () => () => {
      const { current: link } = linkRef;
      const { current: screens } = screensRef;

      const processedLink = !isInternalLink(link, screens) && enableLinkProtocolAppending
        ? appendProtocol(link)
        : link;

      if (props.link !== processedLink) {
        toggleLink(processedLink);
      }
    },
    [],
  );

  React.useEffect(
    () => {
      const uuidRegExp = new RegExp(`${SCREEN_LINK_PREFIX}${UUID_PATTERN.source}`, 'ig');
      isExternalLink && uuidRegExp.test(link) && toggleLink('');
    },
    [],
  );

  const onEnterPress = (event: React.KeyboardEvent): void => {
    if (event.key === KeyboardKey.ENTER) {
      event.isPropagationStopped();
      event.preventDefault();
      toggleDropdown();
    }
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if ((event.ctrlKey || event.metaKey) && validateEventKey(event)) {
      event.stopPropagation();
    }
  };

  const saveLink = (): void => {
    toggleLink(linkText);
  };

  const onExternalLinkClick = (): void => {
    !isExternalLink && toggleLink('');
  };

  const onInternalLinkClick = (screenLink: string): React.ReactEventHandler => () => {
    toggleLink(link === screenLink ? '' : screenLink);
  };

  const renderInternalLinks = (): JSX.Element[] => {
    return screens.valueSeq().toArray().map((screen: ScreenMap, index) => {
      const name = screen.get('name');
      const id = screen.get('id');
      const screenNumber = index + 1;
      const isActive = link.includes(id);

      return (
        <button
          key={name}
          onClick={onInternalLinkClick(`${SCREEN_LINK_PREFIX}${id}`)}
          className={classNames('reset-button', styles.optionItem, styles.screen, { [styles.active]: isActive })}
          data-testid={`${dataTestId}-item${index}`}
        >
          <div className={classNames(styles.screenItem)}>
            <div className={classNames(styles.screenNumber)}>{ isActive ? null : screenNumber}</div>
            <div >{id === activeScreenId ? `${name} ${intlGet(editorToolbarLink, 'CurrentScreen')}` : name}</div>
          </div>
        </button>
      );
    });
  };

  return (
    <Dropdown className={styles.LinkDropdown} stopClickPropagation={true} dataTestId={`${dataTestId}-dropdown`}>
      <div className={styles.title} data-testid={`${dataTestId}-title`}>{intlGet(editorToolbarLink, 'Title')}</div>
      <div className={styles.container} data-testid={`${dataTestId}-container`}>
        {enableInternalLinks && <label className={styles.label} data-testid={`${dataTestId}-label`}>{intlGet(editorToolbarLink, 'URL')}</label>}
        <div
          className={classNames(styles.optionItem, styles.inputOption, { [styles.active]: isExternalLink })}
          onClick={onExternalLinkClick}
          data-testid={`${dataTestId}-externalOption`}
        >
          <input
            autoFocus={true}
            className={styles.input}
            onBlur={saveLink}
            onChange={(e): void => setLinkText(removeZeroWidthSpace(e.target.value.trim()))}
            onKeyPress={onEnterPress}
            onKeyDown={onKeyDown}
            placeholder={externalLinkPlaceholder}
            value={linkText}
            data-testid={`${dataTestId}-input`}
          />
        </div>
        {showInternalLinks &&
          <>
            <label className={styles.label} data-testid={`${dataTestId}-selectDestinationLabel`}>
              {intlGet(editorToolbarLink, 'SelectDestination')}
            </label>
            <div className={styles.optionsList} data-testid={`${dataTestId}-specialOptions`}>
              {showLinkToPreviousScreen &&
                <div
                  className={classNames(styles.optionItem, { [styles.active]: link === PREVIOUS_SLIDE })}
                  onClick={onInternalLinkClick(PREVIOUS_SLIDE)}
                  data-testid={`${dataTestId}-previous`}
                >
                  {intlGet(editorToolbarLink, 'Previous')}
                </div>
              }
              {showLinkToNextScreen &&
                <div
                  className={classNames(styles.optionItem, { [styles.active]: link === NEXT_SLIDE })}
                  onClick={onInternalLinkClick(NEXT_SLIDE)}
                  data-testid={`${dataTestId}-next`}
                >
                  {intlGet(editorToolbarLink, 'Next')}
                </div>
              }
              <div
                className={classNames(styles.optionItem, { [styles.active]: link === LAST_VISITED_SLIDE })}
                onClick={onInternalLinkClick(LAST_VISITED_SLIDE)}
                data-testid={`${dataTestId}-lastVisited`}
              >
                {intlGet(editorToolbarLink, 'LastVisited')}
              </div>
            </div>
            <label className={styles.label} data-testid={`${dataTestId}-screensLabel`}>{intlGet(editorToolbarLink, 'Screens')}</label>
            <div className={styles.optionsList} data-testid={`${dataTestId}-internalList`}>
              {renderInternalLinks()}
            </div>
          </>
        }
      </div>
    </Dropdown>
  );
};

export default LinkDropdown;
